import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';
import PropsTable from '../../components/PropsTable';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const DemoText = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#475569')};
  line-height: 1.6;
  margin: 0;
`;

const Cards = () => {
  const cardProps = [
    {
      name: 'children',
      type: 'node',
      required: false,
      description: 'Content to display inside the card',
    },
    {
      name: 'title',
      type: 'node',
      required: false,
      description: 'Optional title for the card (can be text or JSX with icons)',
    },
    {
      name: 'titleSize',
      type: "'small' | 'normal' | 'large'",
      default: "'normal'",
      required: false,
      description: 'Size of the title text',
    },
    {
      name: 'accent',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Adds orange left border to the entire card',
    },
    {
      name: 'accentTitle',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Adds orange accent bar next to the title',
    },
    {
      name: 'headerBorder',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Adds border under the header',
    },
    {
      name: 'noPadding',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Removes default padding from the card',
    },
    {
      name: 'compact',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Uses compact padding (16px instead of 20px)',
    },
    {
      name: 'fullHeight',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Makes the card fill its parent height',
    },
  ];

  return (
    <div>
      <PageHeader>
        <Title>Cards</Title>
        <Subtitle>
          Cards are the primary container component for grouping related content
          and actions. They provide consistent styling, spacing, and theming
          across the application.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Basic Card</SectionTitle>
        <SectionDescription>
          A simple card with content. Cards automatically adapt to light and
          dark themes.
        </SectionDescription>
        <ComponentDemo label="Default Card" align="stretch">
          <Card style={{ width: '400px', maxWidth: '100%' }}>
            <DemoText>
              This is a basic card with default styling. It has standard padding
              and a subtle background that adapts to the current theme.
            </DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`import Card from 'components/Card';

<Card>
  <p>Card content goes here</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Card with Title</SectionTitle>
        <SectionDescription>
          Cards can have titles for clear content organization.
        </SectionDescription>
        <ComponentDemo label="Card with Title" align="stretch">
          <Card title="Customer Information" style={{ width: '400px', maxWidth: '100%' }}>
            <DemoText>
              Cards with titles help organize content and provide clear context
              for the information displayed within them.
            </DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card title="Customer Information">
  <p>Card content with a title</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Card with Icon in Title</SectionTitle>
        <SectionDescription>
          The title prop accepts JSX, allowing you to include icons alongside text.
        </SectionDescription>
        <ComponentDemo label="Icon in Title" align="stretch">
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg
                  style={{ width: '20px', height: '20px', color: '#FA4616' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span>Customers</span>
              </div>
            }
            style={{ width: '400px', maxWidth: '100%' }}
          >
            <DemoText>
              You can pass JSX to the title prop, making it easy to include
              icons, badges, or other elements alongside the title text.
            </DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card
  title={
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <svg
        style={{ width: '20px', height: '20px', color: '#FA4616' }}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
      <span>Customers</span>
    </div>
  }
>
  <p>Card content with icon in title</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Card with Accent Title</SectionTitle>
        <SectionDescription>
          Add an orange accent bar next to the title to emphasize important
          sections.
        </SectionDescription>
        <ComponentDemo label="Accent Title" align="stretch">
          <Card title="Quick Actions" accentTitle style={{ width: '400px', maxWidth: '100%' }}>
            <DemoText>
              The accent title adds a visual emphasis using our brand color,
              perfect for highlighting key sections or action areas.
            </DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card title="Quick Actions" accentTitle>
  <p>Important card content</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Card with Accent Border</SectionTitle>
        <SectionDescription>
          Add an orange left border to the entire card for strong visual
          emphasis.
        </SectionDescription>
        <ComponentDemo label="Accent Border" align="stretch">
          <Card title="Alert" accent style={{ width: '400px', maxWidth: '100%' }}>
            <DemoText>
              The accent border provides strong visual emphasis, useful for
              highlighting important notifications or priority content.
            </DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card title="Alert" accent>
  <p>Important alert or notification</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Card with Header Border</SectionTitle>
        <SectionDescription>
          Add a separator line between the title and content for clear visual
          hierarchy.
        </SectionDescription>
        <ComponentDemo label="Header Border" align="stretch">
          <Card
            title="Statistics"
            headerBorder
            style={{ width: '400px', maxWidth: '100%' }}
          >
            <DemoText>
              The header border creates clear separation between the title and
              content, useful for cards with multiple sections or complex
              layouts.
            </DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card title="Statistics" headerBorder>
  <p>Card content with header separation</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Compact Card</SectionTitle>
        <SectionDescription>
          Use compact padding (16px) for tighter layouts or when space is
          limited.
        </SectionDescription>
        <ComponentDemo label="Compact Padding" align="stretch">
          <Card title="Compact Card" compact style={{ width: '400px', maxWidth: '100%' }}>
            <DemoText>
              Compact cards use less padding, making them ideal for dense
              interfaces or when displaying multiple cards in a grid.
            </DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card title="Compact Card" compact>
  <p>Tighter padding for dense layouts</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Card without Padding</SectionTitle>
        <SectionDescription>
          Remove all padding for custom content that extends to the card edges.
        </SectionDescription>
        <ComponentDemo label="No Padding" align="stretch">
          <Card
            title="Custom Content"
            noPadding
            style={{ width: '400px', maxWidth: '100%' }}
          >
            <div
              style={{
                padding: '20px',
                background: 'rgba(250, 70, 22, 0.1)',
                borderTop: '1px solid rgba(250, 70, 22, 0.2)',
              }}
            >
              <DemoText>
                No padding cards are useful for tables, images, or custom
                layouts that need to extend to the card edges.
              </DemoText>
            </div>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card title="Custom Content" noPadding>
  <div style={{ padding: '20px' }}>
    Custom content with edge-to-edge layout
  </div>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Title Sizes</SectionTitle>
        <SectionDescription>
          Cards support three title sizes for different hierarchical levels.
        </SectionDescription>
        <ComponentDemo
          label="Title Size Variants"
          direction="column"
          align="stretch"
          gap="24px"
        >
          <Card title="Small Title" titleSize="small" style={{ width: '400px', maxWidth: '100%' }}>
            <DemoText>Card with small title text</DemoText>
          </Card>
          <Card title="Normal Title" titleSize="normal" style={{ width: '400px', maxWidth: '100%' }}>
            <DemoText>Card with normal title text (default)</DemoText>
          </Card>
          <Card title="Large Title" titleSize="large" style={{ width: '400px', maxWidth: '100%' }}>
            <DemoText>Card with large title text</DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card title="Small Title" titleSize="small">
  <p>Content</p>
</Card>

<Card title="Normal Title" titleSize="normal">
  <p>Content</p>
</Card>

<Card title="Large Title" titleSize="large">
  <p>Content</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Card with Icon in Content</SectionTitle>
        <SectionDescription>
          You can also place icons inside the card content for custom layouts
          (pattern used in the Customers page header).
        </SectionDescription>
        <ComponentDemo label="Icon in Content" align="stretch">
          <Card style={{ width: '400px', maxWidth: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(250, 70, 22, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  style={{ width: '32px', height: '32px', color: '#FA4616' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600' }}>
                  Customers
                </h3>
                <DemoText style={{ margin: 0, fontSize: '13px' }}>
                  Manage your customer database
                </DemoText>
              </div>
            </div>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card>
  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
    {/* Icon container */}
    <div style={{
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: 'rgba(250, 70, 22, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <svg style={{ width: '32px', height: '32px', color: '#FA4616' }}>
        {/* icon paths */}
      </svg>
    </div>

    {/* Content */}
    <div>
      <h3>Customers</h3>
      <p>Manage your customer database</p>
    </div>
  </div>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Combined Variants</SectionTitle>
        <SectionDescription>
          Combine multiple props for complex card designs.
        </SectionDescription>
        <ComponentDemo label="Combined Props" align="stretch">
          <Card
            title="Featured Section"
            accentTitle
            headerBorder
            style={{ width: '400px', maxWidth: '100%' }}
          >
            <DemoText>
              This card combines accent title and header border for maximum
              visual emphasis and clear content separation.
            </DemoText>
          </Card>
        </ComponentDemo>
        <CodeBlock>
{`<Card
  title="Featured Section"
  accentTitle
  headerBorder
>
  <p>Important, well-organized content</p>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <SectionDescription>
          All available props for the Card component.
        </SectionDescription>
        <PropsTable props={cardProps} />
      </Section>

      <Section>
        <SectionTitle>Theme Tokens</SectionTitle>
        <SectionDescription>
          Cards use the following theme tokens for consistent styling.
        </SectionDescription>
        <CodeBlock>
{`// Theme tokens used by Card component
const StyledCard = styled.div\`
  // Background
  background: \${props => props.theme.colors.card.background};

  // Border
  border: 1px solid \${props => props.theme.colors.card.border};

  // Shadow
  box-shadow: \${props => props.theme.colors.card.shadow};

  // Border radius
  border-radius: 12px;

  // Hover state
  &:hover {
    box-shadow: \${props => props.theme.colors.card.shadowHover};
  }

  // Accent border (when accent prop is true)
  border-left: 3px solid \${props => props.theme.colors.primary};
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" align="stretch" gap="16px">
          <Card title="✅ Do" accentTitle>
            <DemoText>
              • Use cards to group related content and actions
              <br />
              • Use accent title for important sections like Quick Actions
              <br />
              • Use header border when you need clear visual separation
              <br />
              • Use compact cards in grid layouts or sidebars
              <br />• Keep card content focused and concise
            </DemoText>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <DemoText>
              • Don't nest cards too deeply (max 2 levels)
              <br />
              • Don't use accent border and accent title together (too much
              emphasis)
              <br />
              • Don't put too much content in a single card
              <br />• Don't forget to test cards in both light and dark themes
            </DemoText>
          </Card>
        </ComponentDemo>
      </Section>
    </div>
  );
};

export default Cards;